<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { zonedTimeToUtc } from 'date-fns-tz';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';

// Os números do período, por destinatário (G5). "Sem confirmação da Meta" é um
// balde à parte e nunca aparece como enviada: é o envio de que nenhum status
// chegou, e a Meta não reenvia status (os de antes da captura ficam assim para
// sempre). Antes de `capture_since`, entregue e lida não têm horário: a tela
// diz de quando em diante ele existe.
const props = defineProps({
  // A resposta de GET campanhas/resumo; null enquanto não chegou.
  report: { type: Object, default: null },
  failed: { type: Boolean, default: false },
});

const { t } = useI18n();
const { number, dateTime } = useAnalyticsFormat();

const NUMBERS = [
  { key: 'sent', label: 'CAMPANHAS.NUMBERS.SENT' },
  {
    key: 'no_confirmation',
    label: 'CAMPANHAS.NUMBERS.NO_CONFIRMATION',
    hint: 'CAMPANHAS.NUMBERS.NO_CONFIRMATION_HINT',
  },
  { key: 'delivered', label: 'CAMPANHAS.NUMBERS.DELIVERED' },
  { key: 'read', label: 'CAMPANHAS.NUMBERS.READ' },
  { key: 'replied', label: 'CAMPANHAS.NUMBERS.REPLIED' },
  { key: 'failed', label: 'CAMPANHAS.NUMBERS.FAILED', alert: true },
  {
    key: 'skipped',
    label: 'CAMPANHAS.NUMBERS.SKIPPED',
    hint: 'CAMPANHAS.NUMBERS.SKIPPED_HINT',
  },
  { key: 'opted_out', label: 'CAMPANHAS.NUMBERS.OPTED_OUT', alert: true },
];

const numbers = computed(() => props.report?.numbers || null);
const timezone = computed(() => props.report?.period?.timezone);

const items = computed(() =>
  NUMBERS.map(item => ({
    ...item,
    value: numbers.value?.[item.key] ?? 0,
  }))
);

// A captura de entregue e lida começou depois do início do período, ou ainda
// não começou. Antes dela, entregue e lida contam do mesmo jeito, sem horário:
// vêm do último status que a mensagem guardou (`messages.status`) ou da
// resposta (responder implica ler, e ler implica entregar), como em
// `Reports::Campanhas::Consulta::DEPOIS`.
//
// O período vem só com a data (`'2026-09-01'`), e o dia começa à meia-noite no
// fuso da conta, e não em UTC: lido como UTC, a captura das 22h de 31/08 em
// São Paulo cairia depois do início de 01/09.
const periodStart = period =>
  zonedTimeToUtc(
    `${String(period?.since).slice(0, 10)}T00:00:00`,
    period?.timezone
  ).getTime();

const captureNotice = computed(() => {
  if (!props.report) return '';
  const since = props.report.capture_since;
  if (!since) return t('CAMPANHAS.CAPTURE.NONE');
  return Date.parse(since) > periodStart(props.report.period)
    ? t('CAMPANHAS.CAPTURE.SINCE', { date: dateTime(since, timezone.value) })
    : '';
});
</script>

<template>
  <AnalyticsPanel
    data-test-id="campanhas-numeros"
    :title="t('CAMPANHAS.NUMBERS.TITLE')"
    :description="
      numbers
        ? t(
            'CAMPANHAS.NUMBERS.RECIPIENTS',
            { count: number(numbers.recipients) },
            numbers.recipients
          )
        : ''
    "
  >
    <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.FAILED') }}
    </p>
    <template v-else-if="report">
      <p
        v-if="captureNotice"
        data-test-id="campanhas-captura"
        class="px-3 py-2 mb-0 text-xs rounded-lg bg-n-amber-3 text-n-amber-11"
      >
        {{ captureNotice }}
      </p>
      <p
        v-if="!numbers.recipients"
        data-test-id="campanhas-sem-envio"
        class="mb-0 text-sm text-n-slate-11"
      >
        {{ t('CAMPANHAS.NUMBERS.EMPTY') }}
      </p>
      <dl
        v-else
        class="grid grid-cols-2 gap-x-4 gap-y-3 m-0 sm:grid-cols-4 min-w-0"
      >
        <div
          v-for="item in items"
          :key="item.key"
          :data-test-id="`campanhas-numero-${item.key}`"
          class="flex flex-col gap-0.5 min-w-0"
        >
          <dt class="text-xs text-n-slate-11">{{ t(item.label) }}</dt>
          <dd
            class="m-0 text-xl font-medium tabular-nums"
            :class="
              item.alert && item.value ? 'text-n-ruby-11' : 'text-n-slate-12'
            "
          >
            {{ number(item.value) }}
          </dd>
          <span v-if="item.hint && item.value" class="text-xs text-n-slate-10">
            {{ t(item.hint) }}
          </span>
        </div>
      </dl>
    </template>
  </AnalyticsPanel>
</template>
